<template>
    <div class="advertisement-moderation">
        <div class="advertisement-moderation__header">
            <h1 class="advertisement-moderation__title">Модерация объявлений</h1>
            <button class="update-btn" :class="{ 'update-btn--spinning': syncing }" :disabled="syncing"
                @click="syncStatuses" aria-label="Синхронизировать статусы объявлений"></button>
        </div>

        <div v-if="advertisements.length === 0" class="advertisement-moderation__empty">
            <p>Нет объявлений на модерации</p>
        </div>

        <div v-else class="advertisement-moderation__list">
            <div v-for="advertisement in advertisements" :key="advertisement.id" class="moderation-card">
                <div class="moderation-card__header">
                    <div class="moderation-card__info">
                        <h3 class="moderation-card__title">{{ advertisement.name || 'Без названия' }}</h3>
                        <div class="moderation-card__meta">
                            <span>ID: {{ advertisement.id }}</span>
                            <span v-if="advertisement.campaign_external">Кампания: {{
                                advertisement.campaign_external.name }}</span>
                            <span v-else-if="advertisement.campaign">Кампания: {{ advertisement.campaign.name }}</span>
                            <span>Тип: {{ advertisement.type }}</span>
                            <span>Создана: {{ formatDate(advertisement.created_at) }}</span>
                        </div>
                        <div v-if="advertisement.created_by" class="moderation-card__creator">
                            <strong>Создатель:</strong> {{ advertisement.created_by.email }}
                            <span class="muted">({{ advertisement.created_by.role }})</span>
                        </div>
                        <div class="moderation-card__stats">
                            <span>Экранов: {{ advertisement.advertisements_count }}</span>
                        </div>
                        <div class="moderation-card__creatives">
                            <strong>Креативы:</strong>
                            <span v-if="advertisement.creatives && advertisement.creatives.length > 0">
                                {{advertisement.creatives.map(c => c.name).join(', ')}}
                            </span>
                            <span v-else class="muted">Не указаны</span>
                        </div>
                        <div v-if="advertisement.comment" class="moderation-card__comment">
                            <strong>Комментарий:</strong> {{ advertisement.comment }}
                        </div>
                    </div>
                </div>

                <div class="moderation-card__actions">
                    <div class="action-buttons">
                        <a :href="advertisementLink(advertisement)" class="btn btn--primary">
                            Просмотр и редактирование
                        </a>
                        <template v-if="!advertisement.advertisement_external_id">
                            <span class="muted">Одиночное объявление</span>
                        </template>
                        <template v-else>
                            <span class="muted">Группа: откройте для модерации каждого объявления по отдельности</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatInRtbTZ } from '../../utils/timezones';
export default {
    name: 'AdvertisementModeration',
    props: {
        user: {
            type: Object,
            required: true,
        },
        initialAdvertisements: {
            type: Array,
            default: () => [],
        },
        initialAdvertisementsPagination: {
            type: Object,
            default: undefined,
        },
    },
    data() {
        return {
            advertisements: [],
            syncing: false,
        };
    },
    mounted() {
        this.advertisements = Array.isArray(this.initialAdvertisements) ? [...this.initialAdvertisements] : [];
    },
    methods: {
        async syncStatuses() {
            if (this.syncing) return;
            this.syncing = true;
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const res = await fetch('/api/advertisements/sync-statuses', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                if (data.success) {
                    this.$notify({
                        title: 'Успешно',
                        message: `Обновлено объявлений: ${data.advertisements_updated || 0}`,
                        type: 'success',
                    });
                }
            } catch (e) {
                this.$notify({
                    title: 'Ошибка',
                    message: 'Не удалось обновить статусы',
                    type: 'error',
                });
            } finally {
                this.syncing = false;
            }
        },
        advertisementLink(advertisement) {
            if (advertisement.advertisement_external_id) {
                return `/guaranteed/advertisements/external/${advertisement.advertisement_external_id}/moderation`;
            }
            // Одиночное объявление
            return `/guaranteed/campaigns/${advertisement.campaign_id}/advertisements/${advertisement.id}/moderation`;
        },
        formatDate(dateString) {
            return formatInRtbTZ(dateString);
        },
    },
};
</script>

<!-- styles moved to resources/scss/shared/_moderation.scss (original <style scoped> removed) -->
.advertisement-moderation {
padding: 18px;
}

.advertisement-moderation__header {
margin-bottom: 12px;
display: flex;
gap: 20px;
}

.advertisement-moderation__title {
font-size: 20px;
font-weight: 600;
margin: 0;
}

.advertisement-moderation__empty {
text-align: center;
padding: 40px 0;
color: #666;
}

.advertisement-moderation__list {
display: grid;
gap: 10px;
}

.moderation-card {
display: grid;
grid-template-columns: 1fr 96px auto;
gap: 12px;
align-items: center;
background: #ffffff;
border-radius: 10px;
padding: 12px 14px;
box-shadow: 0 1px 0 rgba(16, 24, 40, 0.03);
border: 1px solid rgba(15, 23, 42, 0.04);
}

.moderation-card__header {
margin-bottom: 16px;
}

.moderation-card__title {
font-size: 15px;
font-weight: 600;
margin: 0;
color: #0f172a;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
}

.moderation-card__meta {
display: flex;
gap: 16px;
margin-bottom: 8px;
font-size: 14px;
color: #666;
flex-wrap: wrap;
}

.moderation-card__creator {
margin-bottom: 8px;
font-size: 14px;
}

.moderation-card__stats {
display: flex;
gap: 16px;
margin: 12px 0;
font-size: 14px;
font-weight: 500;
}

.moderation-card__creatives {
margin-bottom: 8px;
font-size: 14px;
}

.moderation-card__comment {
margin-top: 12px;
padding: 12px;
background: #f5f5f5;
border-radius: 4px;
font-size: 14px;
}

.moderation-card__actions {
padding-top: 16px;
border-top: 1px solid #e6e9ef;
}

.action-buttons {
display: flex;
gap: 12px;
align-items: center;
}

.muted {
color: #999;
}
<!-- end of moved styles -->